import "rxjs/add/observable/combineLatest";
import "rxjs/add/operator/mergeMap";
import { AngularFireDatabase, AngularFireList } from "angularfire2/database";
import { Injectable, Input, OnInit } from "@angular/core";
import { AngularFireAuth } from "angularfire2/auth";
import { AuthService } from "../services/auth.service";
import { BackendService } from "./../services/backend.service";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { Constants } from "./../../helper/constants";
import { Observable } from "rxjs/Observable";
import { Plan } from "./type-setup/plan";
import { PriceDetail } from "./type-setup/price-detail";
import { StringGenerator } from "./../string/generator";
import { TypeSetup } from "./type-setup";

@Injectable()
export class BillTypeService implements OnInit {
  private userId: string;
  subscriberCommunities$;
  communities = [];
  @Input() modalTitle;

  billTypesRef: AngularFireList<{}>;
  billTypes: any;
  private baseCommunityBillTypePath: string;

  typeSetupRef: AngularFireList<TypeSetup>;
  typeSetups: any;
  bsBillTypePlans$: BehaviorSubject<Array<Plan>>;
  userEmail: string;
  currentUserId: string;
  timestamp: number;
  communityBillTypes$: BehaviorSubject<Array<TypeSetup>>;

  constructor(
    private db: AngularFireDatabase,
    private authFire: AngularFireAuth,
    private authService: AuthService,
    private stringGenerator: StringGenerator
  ) {
    this.communityBillTypes$ = new BehaviorSubject([]);
    this.authService.getCurrentUserId().then(userId => {
      this.userId = userId;
    });

    this.userEmail = this.authService.getAuthState().email;
    this.currentUserId = BackendService.generateUserId(
      this.userEmail
    );
    this.timestamp = BackendService.getCurrentTimeStamp();

    this.bsBillTypePlans$ = new BehaviorSubject([]);

    this.billTypesRef = this.db.list(Constants.BILL_TYPES_REF); // ref to list of bill types
    this.billTypesRef
      .snapshotChanges()
      .map(changes => {
        return changes.map(data => ({ ...data.payload.val() }));
      })
      .subscribe(billTypes => {
        this.billTypes = billTypes;
      });

    // get list of community bill type setups
    this.baseCommunityBillTypePath = Constants.COMMUNITY_BILL_TYPE_REF;
    this.typeSetupRef = this.db.list(this.baseCommunityBillTypePath);

    this.typeSetupRef
      .snapshotChanges()
      .map(changes => {
        return changes.map(data => ({
          key: data.payload.key,
          ...data.payload.val()
        }));
      })
      .subscribe(typeSetups => {
        this.typeSetups = typeSetups;
      });
  }

  ngOnInit(): void {
  }

  multicastAvailablePlans(communityId: string, billTypeId: string): void {
    const plansRef = this.db.list(
      Constants.PLANS_REF + "/" + communityId + "/" + billTypeId
    );

    plansRef
      .snapshotChanges()
      .map(changes => {
        return changes.map(data => ({
          id: data.payload.key,
          ...data.payload.val()
        }));
      })
      .subscribe((plans: any) => this.bsBillTypePlans$.next(plans));
  }

  updatePaymentDetails(
    communityId: string,
    billTypeId: string,
    paymentDetail: {},
    compulsory: boolean
  ): boolean {
    if (paymentDetail !== {} && communityId) {
      try {

        const payment = {
          structure: paymentDetail["paymentStructure"],
          method: paymentDetail["paymentMethod"],
          instruction: paymentDetail["paymentInstruction"]
        };

        const communityBillTypePath =
          this.baseCommunityBillTypePath + "/" + communityId + "/" + billTypeId;

        this.db.list(communityBillTypePath).update("payment", payment);

        const communityPath =
          this.baseCommunityBillTypePath + "/" + communityId;

        const updateInfo = {};

        updateInfo["compulsory"] = compulsory;

        updateInfo["updatedAt"] = this.timestamp;
        if (payment.structure === "fixed") {
          updateInfo["isRecurring"] = true;
        }

        this.db.list(communityPath).update(billTypeId, updateInfo);

        return true;
      } catch (e) {
        return false;
      }
    }

    return false;
  }

  updatePlans(communityId: string, billTypeId: string, plans: Array<Plan>): boolean {
    try {
      const plansPath =
        Constants.PLANS_REF + "/" + communityId + "/" + billTypeId;

      if (this.removePreviousPlans(communityId, billTypeId, plansPath)) {
        const plansRef = this.db.list(plansPath);

        for (const plan of plans) {
          if (!plan["id"]) {
            plan.operatorId = this.currentUserId;
            plan.createdAt = this.timestamp;
            plan.updatedAt = this.timestamp;
            plansRef.push(plan).then(data => {
              plan.id = data.key;
              plansRef.update(data.key, plan);
            });
          }
        }

        return true;
      }

      return false;
    } catch (e) {
      console.log(e);

      return false;
    }
  }

  updatePlan(plan: Plan, communityId: string, billTypeId: string): boolean {
    try {
      const plansRef = this.db.list(
        Constants.PLANS_REF + "/" + communityId + "/" + billTypeId
      );

      plan.operatorId = this.currentUserId;
      plan.updatedAt = this.timestamp;
      plansRef.update(plan.id, plan);

      return true;
    } catch (e) {
      return false;
    }
  }

  removePlan(
    communityId: string,
    billTypeId: string,
    planUId: string
  ): boolean {
    let status = false;
    try {
      const plansPath = Constants.PLANS_REF + "/" + communityId + "/" +
        billTypeId + "/" + planUId;
      const planRef = this.db.list(plansPath);
      planRef.remove();

      status = true;
    } catch (error) {
      console.log(error);
    }

    return status;
  }

  updatePrice(
    communityId: string,
    billTypeId: string,
    priceDetail: PriceDetail
  ): boolean {
    let status = false;
    try {
      const pricePath =
        Constants.FLEXIBLE_PRICE_DETAIL_REF +
        "/" +
        communityId +
        "/" +
        billTypeId;
      const priceRef = this.db.list(pricePath);
      priceDetail.operatorId = this.currentUserId;

      if (priceDetail.id) {
        // updating existing price
        priceDetail.updatedAt = this.timestamp;
        priceRef.update(priceDetail.id, priceDetail);
      } else {
        // creating a new price
        priceDetail.createdAt = this.timestamp;
        priceRef.push(priceDetail).then(data => {
          priceDetail.id = data.key;
          priceDetail.updatedAt = this.timestamp;
          priceRef.update(data.key, priceDetail);
        });
      }

      status = true;
    } catch (error) {
      console.log(error);
    }

    return status;
  }

  removePreviousPlans(
    communityId: string,
    billTypeId: string,
    plansPath: string
  ): boolean {
    try {
      const plansRef = this.db.list(plansPath);
      plansRef.remove();

      return true;
    } catch (error) {
      return false;
    }
  }

  getObjectNameWithId(id, items): any {
    for (const item of items) {
      if (item.id === id) {
        return item.name;
      }
    }
  }

  getObjectIdWithName(name, items): any {
    for (const item of items) {
      if (item.name === name) {
        return item.id;
      }
    }
  }

  getCommunitiesByBillTypeId(billtypeId): Observable<{}> {
    this.subscriberCommunities$ = this.db
      .list(`/billtype_community/${billtypeId}`)
      .valueChanges()
      .map(value => {
        return value.map((data: any) => {
          return this.db
            .object(`/communities/${data.id}`)
            .valueChanges()
            .map(communities => communities)
            .map(community => {
              if(community){
                community["status"] = data.status;
                return community;
              }
            });
        });
      })
      .flatMap(communities => Observable.combineLatest(communities));

    return this.subscriberCommunities$;
  }

  async isBillTypeUnique(name): Promise<boolean> {
    const billTypes = await new Promise<any>(resolve => {
      this.getBillTypes().subscribe(result => {
        resolve(result);
      });
    });
    const names = billTypes.map(billType => {
      return billType.name.toLowerCase();
    });

    return names.indexOf(name.toLowerCase()) === -1;
  }

  store(billTypes): void {
    billTypes.map(billType => {
      this.saveBillType(billType).then(result => {
        this.db.list("/billtypes").update(result.key, { id: result.key });
        if (billType.communities) {
          billType.communities.map(community =>
            this.attachBillTypeToCommunity(result.key, billType, community.id)
          );
        }
      });
    });
  }

  update(billTypeId, name): void {
    this.db.list("/billtypes").update(billTypeId, {
      name,
      operatorId: this.userId,
      updatedAt: this.timestamp
    });
  }

  deactivateCommunity(billTypeId, communityId): void {
    const communityBillType = this.db
      .list(`/billtype_community/${billTypeId}`)
      .update(communityId, { status: "disabled" });

    const billTypeCommunity = this.db
      .list(`/community_billtype/${communityId}`)
      .update(billTypeId, { status: "disabled" });

    Promise.all([billTypeCommunity, communityBillType]);
    this.getCommunityBillTypes(communityId);
  }

  reactivateCommunity(billTypeId, communityId): void {
    const communityBillType = this.db
      .list(`/billtype_community/${billTypeId}`)
      .update(communityId, { status: "enabled" });
    const billTypeCommunity = this.db
      .list(`/community_billtype/${communityId}`)
      .update(billTypeId, { status: "enabled" });

    Promise.all([billTypeCommunity, communityBillType]);
    this.getCommunityBillTypes(communityId);
  }

  getBillType(id): Observable<{}> {
    return this.db.object(`/billtypes/${id}`).valueChanges();
  }

  addCommunitiesToBillType(billType, communities): void {
    communities.forEach(community => {
      this.attachBillTypeToCommunity(billType.id, billType, community.id);
    });
  }

  addBillsToCommunity(community, bills): void {
    bills.forEach(bill => {
      this.attachBillTypeToCommunity(bill.id, bill, community.id);
    });
  }

  getFormattedBillTypes(): Array<TypeSetup> {
    return this.billTypes;
  }

  getBillTypes(): Observable<Array<{}>> {
    return this.db.list("/billtypes", ref => ref.orderByChild("name")).valueChanges();
  }

  getServicePrice(communityId: string, serviceId): any {
    const result = this.db.list(`/prices/${communityId}/${serviceId}`).valueChanges();

    return result || false;
  }

  getEnabledBillTypeIds(flatObjects): Array<string> {
    const objects = Object.values(flatObjects);
    const enabledObjects = [];
    for (const object of objects) {
      if (object["status"] === "enabled") {
        enabledObjects.push(object["id"]);
      }
    }

    return enabledObjects;
  }

  getCommunityBillTypes(communityId: string): any {
    const allBillTypes$ = this.db.list("/billtypes").valueChanges();

    const communityBilltypes$ = this.db.list("/community_billtype/" + communityId)
      .valueChanges()
      .map(data => {
        return data.filter(element => element);
      });

    const communityBilltypes = Observable.zip(allBillTypes$, communityBilltypes$,
      (allBillTypes: [any], communityBills: [any]) => {

        const commBilltypes = [];

        for (const data of communityBills) {
          const filtered = allBillTypes.filter(element => element["id"] === data["id"]);
          if (filtered.length) {
            data["name"] = filtered[0]["name"];
            commBilltypes.push(data);
          }
        }

        return this.sort(commBilltypes);
      });
    communityBilltypes.subscribe(data => {
      this.communityBillTypes$.next(data);
    });

    return communityBilltypes;
  }

  sort(items: Array<any>): Array<any> {
    items.sort((a, b) => {
      const nameA = a.name.toUpperCase(); // ignore upper and lowercase
      const nameB = b.name.toUpperCase(); // ignore upper and lowercase
      const result = nameA < nameB ? -1 : nameA > nameB ? 1 : 0;

      return result;
    });

    return items;
  }

  getAllBillTypes(): Observable<any> {
    return this.db.list("/billtypes").valueChanges();
  }

  getAllCommunityBillTypes(communityId: string): any {
    const result = this.db.list("/community_billtype/" + communityId)
      .valueChanges();

    return result || false;
  }

  getBillTypeByRole(activeRole: any): Observable<any> {
    const communityManager = Constants.USER_TYPE_COMMUNITY_MANAGER;
    const admin = Constants.USER_TYPE_ADMIN;
    if (activeRole.role === communityManager) {
      return this.getCommunityBillTypes(activeRole.communityId);
    }

    if (activeRole.role === admin) {
      return this.getBillTypes();
    }
  }
  getBillTypesFromSubscription(communityId, residentId, subscriptionId): Observable<{}> {
    return this.db.object(`/subscriptions/${communityId}/${residentId}/${subscriptionId}/billTypeId`)
      .valueChanges().flatMap(billTypeId => this.db.object(`/billtypes/${billTypeId}/name`).valueChanges());
  }

  private attachBillTypeToCommunity(billTypeId, billType, communityId): void {
    const communityBillType = this.db
      .list("/community_billtype/" + communityId)
      .set(billTypeId, {
        id: billTypeId,
        status: "enabled",
        operatorId: this.userId,
        compulsory: billType.compulsory || false,
        createdAt: BackendService.getCurrentTimeStamp(),
        updatedAt: BackendService.getCurrentTimeStamp()
      });
    const billTypeCommunity = this.db
      .list("/billtype_community/" + billTypeId)
      .set(communityId, {
        id: communityId,
        status: "enabled"
      });
    Promise.all([billTypeCommunity, communityBillType]);
  }

  private async saveBillType(billType): Promise<any> {
    const reference = await this.stringGenerator.generate("services");
    if (reference) {
      return this.db.list("/billtypes").push({
        name: billType.name,
        operatorId: this.userId,
        reference,
        createdAt: this.timestamp,
        updatedAt: this.timestamp
      });
    } else {
      return "";
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/bill/bill-type.service.ts